import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ExecutionList } from '@agentic-os/contracts';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { ErrorNote, Mono, PageHeader, Table, Td, Th } from '@/components/ui/primitives';
import { DomainStatus, StatusBadge } from '@/components/ui/status-badge';
import { api, formatTime } from '@/lib/api';
import { useSession } from '@/lib/session';

interface SystemHealth {
  status: 'ok' | 'degraded' | 'fail';
  checks: Array<{
    name: string;
    status: 'ok' | 'degraded' | 'fail';
    durationMs: number;
    message?: string;
  }>;
  backups: Array<{ kind: string; status: string; startedAt: string; completedAt: string | null }>;
}

export function useSystemHealth(enabled: boolean) {
  return useQuery({
    queryKey: ['system-health'],
    queryFn: () => api<SystemHealth>('/api/v1/system/health', { acceptStatuses: [503] }),
    enabled,
    refetchInterval: 15_000,
  });
}

export function DashboardPage() {
  const { can } = useSession();
  const health = useSystemHealth(can('system.health.read'));
  const recent = useQuery({
    queryKey: ['executions', 'recent'],
    queryFn: () => api<ExecutionList>('/api/v1/executions?limit=8'),
    enabled: can('executions.read'),
    refetchInterval: 15_000,
  });

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title="Dashboard"
        description="Operational state of this Agentic OS environment."
      />
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex items-center gap-3">
            <CardTitle>Platform health</CardTitle>
            <span className="ml-auto" data-testid="overall-status">
              <StatusBadge status={health.data?.status ?? 'unknown'} />
            </span>
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            {health.isError && <ErrorNote error={health.error} />}
            {(health.data?.checks ?? []).map((c) => (
              <div key={c.name} className="flex items-center gap-2">
                <Mono>{c.name}</Mono>
                <span className="ml-auto">
                  <StatusBadge status={c.status} />
                </span>
              </div>
            ))}
            <Link
              to="/system/health"
              className="inline-block pt-1 text-xs text-accent hover:underline"
            >
              System health details →
            </Link>
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Backups</CardTitle>
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            {(health.data?.backups ?? []).length === 0 && (
              <p className="text-muted">No backup or restore test recorded yet.</p>
            )}
            {(health.data?.backups ?? []).map((b) => (
              <div key={b.kind} className="flex items-center gap-2">
                <span className="capitalize">{b.kind.replace('_', ' ')}</span>
                <span className="text-xs text-muted">
                  {formatTime(b.completedAt ?? b.startedAt)}
                </span>
                <span className="ml-auto">
                  <DomainStatus value={b.status} />
                </span>
              </div>
            ))}
          </CardBody>
        </Card>
      </div>
      <Card>
        <CardHeader className="flex items-center">
          <CardTitle>Recent executions</CardTitle>
          <Link to="/executions" className="ml-auto text-xs text-accent hover:underline">
            All executions →
          </Link>
        </CardHeader>
        <CardBody className="p-0">
          {recent.isError ? (
            <div className="p-4">
              <ErrorNote error={recent.error} />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Workflow</Th>
                  <Th>Status</Th>
                  <Th>Principal</Th>
                  <Th>Created</Th>
                </tr>
              </thead>
              <tbody>
                {(recent.data?.items ?? []).map((e) => (
                  <tr key={e.executionId}>
                    <Td>
                      <Link
                        to="/executions/$executionId"
                        params={{ executionId: e.executionId }}
                        className="text-accent hover:underline"
                      >
                        {e.workflow.key} v{e.workflow.version}
                      </Link>
                    </Td>
                    <Td>
                      <DomainStatus value={e.status} />
                    </Td>
                    <Td>{e.principal.email ?? <Mono>{e.principal.userId}</Mono>}</Td>
                    <Td className="whitespace-nowrap text-muted">{formatTime(e.createdAt)}</Td>
                  </tr>
                ))}
                {recent.data?.items.length === 0 && (
                  <tr>
                    <Td colSpan={4} className="text-muted">
                      No executions yet. Run <Mono>execute_test_workflow</Mono> from Claude.
                    </Td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
